import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Phone, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';
import { api, apiErrorMessage } from '../apiClient';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

export default function EnquiryFormPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [name, setName] = useState(user?.full_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [guests, setGuests] = useState('1');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [organizerPhone, setOrganizerPhone] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/events/${eventId}`).then((r) => setEvent(r.data)).catch(() => {});
  }, [eventId]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const g = parseInt(guests, 10);
    if (!name.trim()) return setErr('Please enter your name');
    if (!phone.trim() || phone.trim().length < 6) return setErr('Please enter a valid phone number');
    if (!g || g < 1) return setErr('Guest count must be at least 1');
    setSubmitting(true);
    try {
      const r = await api.post('/enquiries', {
        event_id: eventId,
        name: name.trim(),
        phone: phone.trim(),
        guests_count: g,
        notes: notes.trim() || '',
      });
      setOrganizerPhone(r.data.organizer_phone);
      setSubmitted(true);
      toast.success('Enquiry submitted!');
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && organizerPhone) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-6" data-testid="enquiry-success">
        <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mb-4">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-black text-gray-900">Enquiry Sent!</h2>
        <p className="text-sm text-gray-500 text-center mt-2 max-w-sm">
          Contact {event?.organizer_name} directly to discuss your enquiry.
        </p>
        <div className="flex items-center gap-2.5 bg-pink-50 border border-pink-200 px-5 py-3.5 rounded-2xl mt-6">
          <Phone className="w-5 h-5 text-brand-magenta" />
          <span className="text-lg font-black text-brand-magenta tracking-wide">{organizerPhone}</span>
        </div>
        <a
          data-testid="enquiry-call-again-btn"
          href={`tel:${organizerPhone}`}
          className="mt-4 flex items-center gap-2 bg-brand-magenta hover:bg-pink-700 text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-colors"
        >
          <Phone className="w-4 h-4" /> Call Now
        </a>
        <button
          data-testid="enquiry-view-mine-btn"
          onClick={() => navigate('/enquiries')}
          className="mt-3 w-full max-w-xs bg-white border-2 border-gray-200 text-gray-900 py-3.5 rounded-2xl font-extrabold text-sm hover:bg-gray-50 transition-colors"
        >
          View My Enquiries
        </button>
        <button onClick={() => navigate('/')} className="mt-2 text-brand-magenta font-bold text-sm py-2">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-4" data-testid="enquiry-form-page">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="enquiry-back-btn" onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>
        <h2 className="text-base font-black text-gray-900">Enquiry</h2>
        <div className="w-6" />
      </div>

      {event && (
        <div className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-100 mb-6">
          <img src={event.banner_url} alt="" className="w-16 h-16 rounded-xl object-cover bg-gray-100" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-gray-900 line-clamp-2">{event.title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{dayjs(event.start_datetime).format('MMM D · h:mm A')} · {event.city}</p>
          </div>
        </div>
      )}

      <h1 className="text-xl font-black text-gray-900 tracking-tight">Your details</h1>
      <p className="text-sm text-gray-500 mt-1 mb-4">The organizer will use this to contact you back.</p>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="enq-name" className="text-xs font-bold text-gray-500">Full name</label>
          <input id="enq-name" data-testid="enquiry-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="enq-phone" className="text-xs font-bold text-gray-500">Phone</label>
          <input id="enq-phone" data-testid="enquiry-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" type="tel" className="w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="enq-guests" className="text-xs font-bold text-gray-500">Guests count</label>
          <input id="enq-guests" data-testid="enquiry-guests-input" value={guests} onChange={(e) => setGuests(e.target.value.replace(/[^0-9]/g, ''))} placeholder="1" type="text" inputMode="numeric" maxLength={3} className="w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="enq-notes" className="text-xs font-bold text-gray-500">Notes (optional)</label>
          <textarea id="enq-notes" data-testid="enquiry-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any special requests or questions?" rows={3} className="w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none" />
        </div>

        {err && (
          <div data-testid="enquiry-error" className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-semibold">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {err}
          </div>
        )}

        <button
          data-testid="enquiry-submit-btn"
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 bg-brand-magenta hover:bg-pink-700 disabled:opacity-70 text-white py-4 rounded-2xl font-black text-base transition-colors mt-2"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Phone className="w-4 h-4" /> Submit & Call Organizer</>}
        </button>

        <p className="text-[11px] text-gray-400 text-center mt-1">By submitting, you agree that the organizer may contact you via phone.</p>
      </form>
    </div>
  );
}
